/**
 * HALO Button Component
 *
 * A systematic button component following Constellation v10 patterns.
 * Supports three main usage patterns:
 * 1. Circle buttons (icon-only via children)
 * 2. Rectangle buttons (text-only via children)
 * 3. Rectangle buttons (icon + text via icon prop + children)
 *
 * Features:
 * - Systematic tone + emphasis color combinations
 * - Responsive fluid width for rectangles
 * - Icon positioning (start/end)
 * - Full accessibility support
 * - Loading states with spinner
 */

import React from 'react';
import { buttonBase, buttonVariants, loadingSpinner, iconWrapper } from './button.styles';
import type { ButtonProps, ResponsiveVariant } from './types';

/**
 * Resolve responsive variant values to current breakpoint value
 * This is a simplified version - in production you might use a responsive hook
 */
function resolveResponsiveValue<T>(value: ResponsiveVariant<T>): T {
  if (typeof value === 'object' && value !== null && 'base' in value) {
    // For now, use base value. In production, this would check current breakpoint
    const responsiveValue = value as { base?: T; sm?: T; md?: T; lg?: T; xl?: T; xxl?: T };
    return responsiveValue.base ?? (Object.values(responsiveValue)[0] as T);
  }
  return value as T;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      shape = 'rectangle',
      tone = 'brand',
      emphasis = 'filled',
      size = 'md',
      fluid = false,
      icon,
      iconPosition = 'start',
      disabled = false,
      loading = false,
      onClick,
      children,
      className,
      ...restProps
    },
    ref,
  ) => {
    // Resolve responsive values
    const resolvedFluid = resolveResponsiveValue(fluid);

    // For circle buttons, ignore icon prop and fluid prop
    const actualFluid = shape === 'circle' ? false : resolvedFluid;
    const actualIcon = shape === 'circle' ? undefined : icon;

    // Combine base styles with variants
    const buttonClasses = [
      buttonBase,
      buttonVariants({
        shape,
        tone,
        emphasis,
        size,
        fluid: actualFluid,
        iconPosition: actualIcon ? iconPosition : undefined,
      }),
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Handle click events
    const handleClick = () => {
      if (!disabled && !loading && onClick) {
        onClick();
      }
    };

    // Handle keyboard events for accessibility
    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClick();
      }
    };

    // Render loading spinner
    const renderSpinner = () => (
      <span className={iconWrapper}>
        <span className={loadingSpinner} aria-hidden="true" />
      </span>
    );

    // Render icon with proper wrapper
    const renderIcon = (iconElement: React.ReactNode) => (
      <span className={iconWrapper} aria-hidden="true">
        {iconElement}
      </span>
    );

    // Render button content based on shape and props
    const renderContent = () => {
      if (loading) {
        return (
          <>
            {renderSpinner()}
            <span style={{ opacity: 0.7 }}>{children}</span>
          </>
        );
      }

      // Circle buttons: always use children for icon content
      if (shape === 'circle') {
        return renderIcon(children);
      }

      // Rectangle buttons with icon + text
      if (actualIcon && shape === 'rectangle') {
        return (
          <>
            {renderIcon(actualIcon)}
            <span>{children}</span>
          </>
        );
      }

      // Rectangle buttons with text only
      return <span>{children}</span>;
    };

    return (
      <button
        ref={ref}
        type="button"
        className={buttonClasses}
        disabled={disabled || loading}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-disabled={disabled || loading}
        aria-busy={loading}
        {...restProps}
      >
        {renderContent()}
      </button>
    );
  },
);

Button.displayName = 'Button';
